import React from "react";
import CustomModalV1 from "components/custom-modal/CustomModalV1";
import Button from "components/button/Button";
import { XIconV1, ExternalLinkIconV1, AlertCircleIconV1 } from "images/image-component";
import {
  markOnboardingSource,
  OnboardingSource,
} from "features/stripe/onboardingReturn";
import { OnboardingVariant } from "features/stripe/settlementRouting";
import "./stripe-onboarding-modal.css";

interface Props {
  isOpen: boolean;
  onboardingUrl: string;
  // Which flow opened this modal, so Stripe's return can be routed back to it.
  source: OnboardingSource;
  // "required": payouts are blocked until setup completes.
  // "optional":  payouts already work; Stripe wants more before a deadline.
  variant?: OnboardingVariant;
  onClose: () => void;
  // Only meaningful for the "optional" variant, where the merchant can carry on
  // without doing anything. Given a handler, the dismiss button runs it instead
  // of just closing — the redeem flow uses this to continue to the redeem modal.
  onSkip?: () => void;
  skipLabel?: string;
}

const COPY: Record<
  OnboardingVariant,
  { title: string; desc: string; cta: string; defaultSkipLabel: string }
> = {
  required: {
    title: "Payout Account Required",
    desc:
      "To redeem your balance, you need to complete your payout account " +
      "setup. It only takes a few minutes and is handled securely by Stripe.",
    cta: "Complete Setup",
    defaultSkipLabel: "Cancel",
  },
  optional: {
    title: "Additional Information Needed",
    desc:
      "Your payout account is active and ready to use. Stripe still needs " +
      "some additional information — if it isn't provided, some of your " +
      "account capabilities may be disabled later on.",
    cta: "Provide Information",
    defaultSkipLabel: "Later",
  },
};

function StripeOnboardingModal({
  isOpen,
  onboardingUrl,
  source,
  variant = "required",
  onClose,
  onSkip,
  skipLabel,
}: Props) {
  const copy = COPY[variant];

  // Navigate in THIS tab, not a new one: Stripe's return_url has to land back in
  // the tab the merchant is using, otherwise the dashboard they came from sits
  // stale behind an orphan tab and never re-checks the account. Mark the source
  // here rather than when the modal opens — then it's recorded if and only if we
  // actually leave for Stripe, so cancelling can't strand a stale marker.
  function handleSetup() {
    if (!onboardingUrl) return;
    markOnboardingSource(source);
    window.location.assign(onboardingUrl);
  }

  return (
    <CustomModalV1 isOpen={isOpen} handleClose={onClose}>
      <button className="v1-modal-close" onClick={onClose} aria-label="Close">
        <XIconV1 />
      </button>

      <div className="soa-modal">
        <div
          className={`soa-modal__icon${variant === "optional" ? " soa-modal__icon--optional" : ""}`}
        >
          {variant === "optional" ? (
            <AlertCircleIconV1 size={22} />
          ) : (
            <ExternalLinkIconV1 size={22} />
          )}
        </div>

        <h3 className="soa-modal__title">{copy.title}</h3>

        <p className="soa-modal__desc">{copy.desc}</p>

        <div className="soa-modal__actions">
          <Button
            variant="primary"
            icon={<ExternalLinkIconV1 size={13} />}
            onClick={handleSetup}
          >
            {copy.cta}
          </Button>
          <Button variant="ghost" onClick={onSkip ?? onClose}>
            {skipLabel ?? copy.defaultSkipLabel}
          </Button>
        </div>
      </div>
    </CustomModalV1>
  );
}

export default StripeOnboardingModal;
